import { TrackingProvider } from '../types/providers';
import { TrackingEvent, EventPriority, serialize } from '../types/tracking-event';
import { TrackingMetadata } from '../types/metadata';

const DEFAULT_ENDPOINT = '/v2/api/v1/user-event-tracking/track-event';

export interface BackendProxyProviderConfig {
  /**
   * Endpoint that relays tracking events to the backend UserEventTrackingController.
   * Defaults to `/v2/api/v1/user-event-tracking/track-event`.
   */
  endpoint?: string;

  /**
   * Whether to prefer the Beacon API for delivery.
   * If `false`, the provider will always use `fetch`.
   */
  useSendBeacon?: boolean;

  /**
   * Extra headers to attach when falling back to `fetch`.
   */
  headers?: Record<string, string>;
}

export class BackendProxyProvider implements TrackingProvider {
  readonly name = 'backend-proxy';

  private readonly endpoint: string;
  private readonly useSendBeacon: boolean;
  private readonly headers: Record<string, string>;

  constructor(private readonly config: BackendProxyProviderConfig = {}) {
    this.endpoint = config.endpoint ?? DEFAULT_ENDPOINT;
    this.useSendBeacon = config.useSendBeacon ?? true;
    this.headers = config.headers ?? {};
  }

  getConfig(): BackendProxyProviderConfig {
    return {
      endpoint: this.endpoint,
      useSendBeacon: this.useSendBeacon,
      headers: this.headers,
    };
  }

  initialize(): void {
    // No-op: nothing to initialize for HTTP relay
  }

  destroy(): void {
    // No-op: nothing to tear down
  }

  async receivedEvent(event: TrackingEvent, metadata: TrackingMetadata): Promise<void> {
    const payload = serialize(this.normalizeIdentityPayload(event), metadata);

    if (this.useSendBeacon && this.trySendBeacon(payload)) {
      return;
    }

    await this.sendWithFetch(payload);
  }

  private normalizeIdentityPayload(event: TrackingEvent): TrackingEvent {
    if (!event.payload || typeof event.payload !== 'object' || Array.isArray(event.payload)) {
      return event;
    }

    const payload = { ...(event.payload as Record<string, unknown>) };
    const normalizedDistinctId = this.normalizeIdentityValue(payload['distinct_id'] ?? payload['distinctId']);

    if (normalizedDistinctId) {
      payload['distinct_id'] = normalizedDistinctId;
    } else {
      delete payload['distinct_id'];
    }
    delete payload['distinctId'];

    const normalizedUserId = this.normalizeIdentityValue(payload['userId']);
    if (normalizedUserId) {
      payload['userId'] = normalizedUserId;
    } else {
      delete payload['userId'];
    }

    const normalizedAnonDistinctId = this.normalizeIdentityValue(payload['$anon_distinct_id']);
    if (normalizedAnonDistinctId) {
      payload['$anon_distinct_id'] = normalizedAnonDistinctId;
    } else {
      delete payload['$anon_distinct_id'];
    }

    return {
      ...event,
      payload,
    };
  }

  private normalizeIdentityValue(value: unknown): string | undefined {
    if (typeof value !== 'string' && typeof value !== 'number') {
      return undefined;
    }

    const normalized = String(value).trim();
    return normalized.length > 0 ? normalized : undefined;
  }

  private trySendBeacon(body: string): boolean {
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
      return false;
    }

    try {
      return navigator.sendBeacon(this.endpoint, body);
    } catch (error) {
      console.warn('sendBeacon failed, falling back to fetch:', error);
      return false;
    }
  }

  private async sendWithFetch(body: string): Promise<void> {
    if (typeof fetch === 'undefined') {
      console.warn('Fetch API is not available; unable to relay tracking event.');
      return;
    }

    try {
      await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.headers,
        },
        body,
        keepalive: true,
      });
    } catch (error) {
      console.error('Failed to relay tracking event to backend:', error);
    }
  }
}
